import type { IReportMediaOptions } from '../types/reportsInterfaces';

export type AiOverlayBox = {
	name: string;
	skuId?: string;
	bbox: [number, number, number, number];
	sim?: number;
	conf?: string;
	faces?: number | null;
	price?: number | null;
};

export type AiOverlayPhoto = {
	mediaId: number | string;
	w: number;
	h: number;
	boxes: AiOverlayBox[];
};

const OVERLAY_CLASS = 'ai-photo-overlay';
const BOX_CLASS = 'ai-photo-overlay__box';
const TIP_CLASS = 'ai-photo-overlay__tip';
const BTN_ACTIVE = 'photoviewer-button-aiRecognition--active';

let enabled = true;
let rafId = 0;
let stageEl: HTMLElement | null = null;
let currentPhoto: AiOverlayPhoto | null = null;

const landscapeIcon = `<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1.5 3.5A1.5 1.5 0 0 1 3 2h10a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 13 14H3a1.5 1.5 0 0 1-1.5-1.5v-9zm1 0v9A.5.5 0 0 0 3 13h10a.5.5 0 0 0 .5-.5v-9A.5.5 0 0 0 13 3H3a.5.5 0 0 0-.5.5z"/><path fill="currentColor" d="M4.5 6.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm7.04 5.02-2.2-2.75a.5.5 0 0 0-.78 0L6.4 11.3l-.72-.9a.5.5 0 0 0-.78.05l-1.1 1.45A.5.5 0 0 0 4.2 13h7.6a.5.5 0 0 0 .34-.88z"/></svg>`;

const formatPrice = (price: number | null | undefined): string | null => {
	if (price == null || Number.isNaN(Number(price))) {
		return null;
	}
	return `${Number(price)} Р`;
};

const formatSim = (sim: number | null | undefined): string | null => {
	if (sim == null || Number.isNaN(Number(sim))) {
		return null;
	}
	return `sim ${Number(sim).toFixed(3)}`;
};

const normalizeBbox = (
	bbox: number[]
): [number, number, number, number] | null => {
	if (!Array.isArray(bbox) || bbox.length < 4) {
		return null;
	}
	const [a, b, c, d] = bbox.map(Number);
	if ([a, b, c, d].some((n) => Number.isNaN(n))) {
		return null;
	}
	return [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];
};

/** Собирает фото AI с боксами + faces/price из skus/pricetags. */
export const buildAiOverlayPhotos = (aiReview: any): AiOverlayPhoto[] => {
	if (!aiReview || !Array.isArray(aiReview.photos)) {
		return [];
	}

	const skus = Array.isArray(aiReview.skus) ? aiReview.skus : [];

	return aiReview.photos
		.map((photo: any) => {
			const mediaId = photo?.mediaId ?? photo?.media?.id;
			if (mediaId == null) {
				return null;
			}
			const w = Number(photo.w) || 0;
			const h = Number(photo.h) || 0;
			const rawBoxes = Array.isArray(photo.boxes) ? photo.boxes : [];
			const pricetags = Array.isArray(photo.pricetags)
				? photo.pricetags
				: [];

			const boxes: AiOverlayBox[] = rawBoxes
				.map((box: any) => {
					const bbox = normalizeBbox(box?.bbox);
					if (!bbox || !box?.name) {
						return null;
					}
					const skuId = box.skuId != null ? String(box.skuId) : undefined;
					const sku = skus.find(
						(s: any) =>
							(skuId && String(s.skuId) === skuId) ||
							s?.name === box.name
					);
					const appears = (sku?.appearsIn || []).find(
						(item: { mediaId?: number | string }) =>
							String(item.mediaId) === String(mediaId)
					);
					const tag = pricetags.find(
						(t: any) =>
							(skuId && String(t.skuId) === skuId) ||
							t?.name === box.name
					);
					const facesFromBoxes = rawBoxes.filter(
						(b: any) =>
							(skuId && String(b.skuId) === skuId) ||
							b?.name === box.name
					).length;
					return {
						name: String(box.name),
						skuId,
						bbox,
						sim: typeof box.sim === 'number' ? box.sim : undefined,
						conf: box.conf,
						faces:
							appears?.facesHere ??
							sku?.faces ??
							(facesFromBoxes || null),
						price:
							typeof tag?.price === 'number'
								? tag.price
								: typeof sku?.price === 'number'
									? sku.price
									: null,
					} as AiOverlayBox;
				})
				.filter(Boolean) as AiOverlayBox[];

			pricetags.forEach((tag: any) => {
				const bbox = normalizeBbox(tag?.bbox);
				if (!bbox) {
					return;
				}
				const skuId = tag.skuId != null ? String(tag.skuId) : undefined;
				const sku = skus.find(
					(s: any) => skuId && String(s.skuId) === skuId
				);
				boxes.push({
					name: sku?.name
						? `Ценник: ${sku.name}`
						: `Ценник${skuId ? ` #${skuId}` : ''}`,
					skuId,
					bbox,
					price:
						typeof tag.price === 'number'
							? tag.price
							: typeof sku?.price === 'number'
								? sku.price
								: null,
					faces: null,
					sim: undefined,
				});
			});

			if (!boxes.length) {
				return null;
			}

			return { mediaId, w, h, boxes };
		})
		.filter(Boolean) as AiOverlayPhoto[];
};

/** Находит AI-оверлей фото по mediaId (для превью в сетке). */
export const findAiOverlayPhoto = (
	aiReview: any,
	mediaId: number | string | undefined | null
): AiOverlayPhoto | null => {
	if (mediaId == null) {
		return null;
	}
	const photos = buildAiOverlayPhotos(aiReview);
	return (
		photos.find((photo) => String(photo.mediaId) === String(mediaId)) || null
	);
};

const matchMediaIdBySrc = (
	src: string,
	medias: IReportMediaOptions[]
): string | null => {
	if (!src || !medias?.length) {
		return null;
	}
	const found = medias.find((media) => {
		const link = media?.url?.link;
		if (!link) {
			return false;
		}
		return src.includes(link) || link.includes(src.split('?')[0]);
	});
	return found?.media?.id != null ? String(found.media.id) : null;
};

const findOverlayPhoto = (
	photos: AiOverlayPhoto[],
	medias: IReportMediaOptions[],
	src: string
): AiOverlayPhoto | null => {
	const mediaId = matchMediaIdBySrc(src, medias);
	if (mediaId) {
		const byId = photos.find((p) => String(p.mediaId) === mediaId);
		if (byId) {
			return byId;
		}
	}
	// fallback: ai-review photo.url
	return null;
};

const clearOverlay = () => {
	if (!stageEl) {
		return;
	}
	stageEl.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove());
};

const buildTipHtml = (box: AiOverlayBox): string => {
	const lines = [`<div class="${TIP_CLASS}-name">${escapeHtml(box.name)}</div>`];
	if (box.faces != null) {
		lines.push(
			`<div class="${TIP_CLASS}-row">Фейсов: ${escapeHtml(String(box.faces))}</div>`
		);
	}
	const price = formatPrice(box.price);
	if (price) {
		lines.push(
			`<div class="${TIP_CLASS}-row">Цена: ${escapeHtml(price)}</div>`
		);
	}
	const sim = formatSim(box.sim);
	if (sim) {
		lines.push(`<div class="${TIP_CLASS}-row">${escapeHtml(sim)}</div>`);
	}
	return lines.join('');
};

const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');

const renderBoxes = (overlay: HTMLElement, photo: AiOverlayPhoto) => {
	overlay.innerHTML = '';
	photo.boxes.forEach((box) => {
		const [x1, y1, x2, y2] = box.bbox;
		const el = document.createElement('div');
		el.className = BOX_CLASS;
		el.style.left = `${(x1 / photo.w) * 100}%`;
		el.style.top = `${(y1 / photo.h) * 100}%`;
		el.style.width = `${((x2 - x1) / photo.w) * 100}%`;
		el.style.height = `${((y2 - y1) / photo.h) * 100}%`;

		const tip = document.createElement('div');
		tip.className = TIP_CLASS;
		tip.innerHTML = buildTipHtml(box);
		el.appendChild(tip);
		overlay.appendChild(el);
	});
};

const syncOverlayToImage = () => {
	if (!stageEl || !enabled || !currentPhoto) {
		clearOverlay();
		return;
	}
	const img = stageEl.querySelector(
		'.photoviewer-image'
	) as HTMLImageElement | null;
	if (!img || !img.complete) {
		return;
	}

	const stageRect = stageEl.getBoundingClientRect();
	const imgRect = img.getBoundingClientRect();
	if (imgRect.width < 2 || imgRect.height < 2) {
		return;
	}

	let overlay = stageEl.querySelector(
		`.${OVERLAY_CLASS}`
	) as HTMLElement | null;
	if (!overlay) {
		overlay = document.createElement('div');
		overlay.className = OVERLAY_CLASS;
		stageEl.appendChild(overlay);
		renderBoxes(overlay, currentPhoto);
	}

	overlay.style.display = 'block';
	overlay.style.left = `${imgRect.left - stageRect.left}px`;
	overlay.style.top = `${imgRect.top - stageRect.top}px`;
	overlay.style.width = `${imgRect.width}px`;
	overlay.style.height = `${imgRect.height}px`;
};

const startSyncLoop = () => {
	stopSyncLoop();
	const tick = () => {
		syncOverlayToImage();
		rafId = window.requestAnimationFrame(tick);
	};
	rafId = window.requestAnimationFrame(tick);
};

const stopSyncLoop = () => {
	if (rafId) {
		window.cancelAnimationFrame(rafId);
		rafId = 0;
	}
};

const setButtonActive = (active: boolean) => {
	const btn = document.querySelector(
		'.photoviewer-button-aiRecognition'
	) as HTMLElement | null;
	if (!btn) {
		return;
	}
	btn.classList.toggle(BTN_ACTIVE, active);
	btn.setAttribute('aria-pressed', active ? 'true' : 'false');
};

const resolveSrcFromContext = (context: any): string => {
	const fromImage = context?.imageData?.src || context?.imageData?.url;
	if (fromImage) {
		return String(fromImage);
	}
	const img = document.querySelector(
		'.photoviewer-stage .photoviewer-image'
	) as HTMLImageElement | null;
	return img?.currentSrc || img?.src || '';
};

export type AttachAiOverlayArgs = {
	context: any;
	medias: IReportMediaOptions[];
	aiReview: any;
	/** Показывать оверлей по умолчанию */
	defaultEnabled?: boolean;
};

/** Подключает оверлей распознавания к открытому PhotoViewer. */
export const attachAiPhotoOverlay = ({
	context,
	medias,
	aiReview,
	defaultEnabled = true,
}: AttachAiOverlayArgs): void => {
	detachAiPhotoOverlay();

	const photos = buildAiOverlayPhotos(aiReview);
	if (!photos.length) {
		return;
	}

	stageEl =
		(document.querySelector('.photoviewer-stage') as HTMLElement) || null;
	if (!stageEl) {
		return;
	}

	enabled = defaultEnabled;
	setButtonActive(enabled);

	const applyForSrc = (src: string) => {
		currentPhoto = findOverlayPhoto(photos, medias, src);
		clearOverlay();
		if (enabled && currentPhoto) {
			const overlay = document.createElement('div');
			overlay.className = OVERLAY_CLASS;
			stageEl!.appendChild(overlay);
			renderBoxes(overlay, currentPhoto);
			syncOverlayToImage();
		}
	};

	applyForSrc(resolveSrcFromContext(context));
	startSyncLoop();
};

export const refreshAiPhotoOverlay = (context: any, medias: IReportMediaOptions[], aiReview: any) => {
	const photos = buildAiOverlayPhotos(aiReview);
	if (!stageEl) {
		stageEl =
			(document.querySelector('.photoviewer-stage') as HTMLElement) ||
			null;
	}
	currentPhoto = findOverlayPhoto(
		photos,
		medias,
		resolveSrcFromContext(context)
	);
	clearOverlay();
	if (enabled && currentPhoto && stageEl) {
		const overlay = document.createElement('div');
		overlay.className = OVERLAY_CLASS;
		stageEl.appendChild(overlay);
		renderBoxes(overlay, currentPhoto);
		syncOverlayToImage();
	}
};

export const toggleAiPhotoOverlay = (): boolean => {
	enabled = !enabled;
	setButtonActive(enabled);
	if (!enabled) {
		clearOverlay();
	} else if (currentPhoto && stageEl) {
		const overlay = document.createElement('div');
		overlay.className = OVERLAY_CLASS;
		stageEl.appendChild(overlay);
		renderBoxes(overlay, currentPhoto);
		syncOverlayToImage();
	}
	return enabled;
};

export const detachAiPhotoOverlay = () => {
	stopSyncLoop();
	clearOverlay();
	stageEl = null;
	currentPhoto = null;
};

export const getAiRecognitionToolbarOptions = () => ({
	footerToolbar: [
		'zoomOut',
		'zoomIn',
		'aiRecognition',
		'prev',
		'fullscreen',
		'next',
		'actualSize',
		'rotateLeft',
		'rotateRight',
	] as string[],
	customButtons: {
		aiRecognition: {
			text: landscapeIcon,
			title: 'Распознавание ИИ',
			click: () => {
				toggleAiPhotoOverlay();
			},
		},
	},
	icons: {
		aiRecognition: landscapeIcon,
	},
	i18n: {
		aiRecognition: 'Распознавание ИИ',
	},
});
